import {Button, Toolbar, ToolbarItem, ToolbarSeparator, ToolbarSpacer} from "@progress/kendo-react-buttons"
import {ComboBoxChangeEvent, DropDownListChangeEvent} from "@progress/kendo-react-dropdowns"
import {Field, Form, FormElement, FormRenderProps} from "@progress/kendo-react-form"
import {Input} from "@progress/kendo-react-inputs"
import dayjs from "dayjs"
import {
    MDBBtn,
    MDBCol,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRow,
} from "mdbreact"
import React, {useEffect, useMemo, useRef} from "react"
import {useLocation} from "react-router-dom"
import {fetchSchedulesForValidation, setToastr} from "../../containers/App/appSlice"
import {
    BookingDetail,
    BookingStatus,
    TestStation,
    checkBlobCertificateExists,
    fetchBookingDetail,
    fetchDVSAAvailability,
    fetchModelData,
    fetchPackageDetails,
    fetchTestPackages,
    fetchVehicleData,
    getCustomPriceForBooking,
    saveBookingDetails,
    sendConfirmationEmail,
    setBlobExists,
    setBookingDetail,
    setCustomers,
    setTestsData,
    showCustomPriceForBooking,
    toggleTestDialog,
} from "../../containers/MotBooking/bookingSlice"
import {RootState} from "../../rootReducer"
import {SpinnerFragment} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {calltoPreparerDealerName} from "../../utils/common"
import DeleteContactModal from "../Customer/CustomerContactGrid/DeleteContactModal"
import AlertToastr from "../Shared/alertToastr"
import BKAxleComboBox from "./controls/axleCodeMultiCombo"
import BKComboBox from "./controls/comboboxField"
import ContactInputField from "./controls/contactInputField"
import CustomerPackagesMultiCombo from "./controls/customerMultiCombo"
import BKDropdownField from "./controls/dropdownField"
import LaneFeesNumberInput from "./controls/laneFeesNumberInput"
import NumberInputField from "./controls/numberInputField"
import BKPreparerIDComboBox from "./controls/preparerIDMultiCombo"
import BKTestCodeComboBox from "./controls/testCodeMultiCombo"
import TestDateSlotSelection from "./controls/testDateSlotSelection"
import TestPackagesMultiCombo from "./controls/testPackagesMultiCombo"
import TextAreaInput from "./controls/textAreaInputField"
import TextInputField from "./controls/textInputField"
import BookingChangeInfoModal from "./testDialogComponents/BookingChangeInfoModal"
import TestBookingFooter from "./testDialogComponents/TestBookingFooter"
import VehicleSection from "./testDialogComponents/VehicleSection"

const TestBookingAddEditModal = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()

    const bookingId = useAppSelector((state: RootState) => state.containers.motBooking.selectedBookingId)
    const showTestDialog = useAppSelector((state: RootState) => state.containers.motBooking.showTestDialog)

    const loadingBookingStatus = useAppSelector((state: RootState) => state.containers.motBooking.gettingBookingStatus)
    const bookingStatusData = useAppSelector((state: RootState) => state.containers.motBooking.bookingStatusList)

    const userDealers = useAppSelector((state: RootState) => state.application.userDealers)
    const booking = useAppSelector((state: RootState) => state.containers.motBooking.bookingDetail)
    const bookingNoEdit = useAppSelector((state: RootState) => state.containers.motBooking.bookingDetailNoEdit)

    const loadingBookingDetail = useAppSelector((state: RootState) => state.containers.motBooking.loadingBookingDetail)
    const loadingTestStations = useAppSelector((state: RootState) => state.containers.motBooking.gettingTestStations)
    const testStations = useAppSelector((state: RootState) => state.containers.motBooking.testStations)
    const transactionTypes = useAppSelector((state: RootState) => state.containers.motBooking.transactionTypes)
    const savingBooking = useAppSelector((state: RootState) => state.containers.motBooking.savingBooking)
    const selectBookingDealer = useAppSelector((state: RootState) => state.containers.motBooking.bookingDetail.dealerID)
    const BlobExists = useAppSelector((state: RootState) => state.containers.motBooking.blobExists)

    const showDltPopover = useAppSelector((state: RootState) => state.containers.customer.showDeletePopover)
    const customerContacId = useAppSelector((state: RootState) => state.containers.customer.customerContacId)

    const bookingDate = dayjs(booking.testStart).startOf("date").format("DD/MM/YYYY")

    const formRef = useRef<any>(null)

    const emailRegex: RegExp = new RegExp(/\S+@\S+\.\S+/)

    // initital Loading Fetch Calls

    useEffect(() => {
        if (showTestDialog && bookingId) {
            if (bookingId > 0) dispatch(fetchBookingDetail(bookingId))
            dispatch(setCustomers([]))
        }
    }, [dispatch, showTestDialog, bookingId])

    // load Vehicle Details for New Booking if Vehicle Is Selected
    useEffect(() => {
        if (bookingId !== null && bookingId === 0 && booking?.registration?.length > 0) {
            dispatch(fetchVehicleData(booking.registration, booking))
        }
    }, [dispatch, bookingId, bookingDate])

    // Get Vehicle Models Data by Make
    useEffect(() => {
        if (booking?.make?.length > 0) {
            dispatch(fetchModelData(booking.make))
        }
    }, [dispatch, booking.make])

    // Get TestPackage when t DealerID is changed.
    useEffect(() => {
        if (booking.dealerID > 0 && booking.bookingTypeID === 1) {
            dispatch(
                fetchTestPackages(
                    booking.dealerID,
                    booking.axleCode ?? "",
                    booking.testCode ?? "",
                    dayjs(booking.testStart).format("YYYY-MM-DD"),
                ),
            )
        }
    }, [dispatch, booking.dealerID, booking.axleCode, booking.testCode, booking.bookingTypeID, booking.testStart])

    // Get Package Details by TestPackageID
    useEffect(() => {
        if (booking.dealerID > 0 && booking.testPackageID && booking.testPackageID > 0)
            dispatch(fetchPackageDetails(booking.dealerID, booking.testPackageID))
        else {
            dispatch(setTestsData([]))
        }
    }, [booking.dealerID, booking.testPackageID, dispatch])

    // Get DVSA Availability for date
    useEffect(() => {
        if (bookingDate?.length > 0 && booking.dealerID > 0) {
            dispatch(fetchDVSAAvailability(booking.dealerID, booking.testStart.toString()))
        }
    }, [dispatch, booking.dealerID, bookingDate])

    // SchedulesForMOT DatePicker Control Booking
    useEffect(() => {
        if (selectBookingDealer && selectBookingDealer > 0) {
            dispatch(fetchSchedulesForValidation(selectBookingDealer.toString(), true))
        }
    }, [selectBookingDealer])

    const getFormInputValues = (booking: BookingDetail) => {
        const formInput = formRef.current.values as BookingDetail

        let preparerID =
            booking.preparerDealerName === "NA"
                ? "NA"
                : booking.preparerDealerName !== ""
                ? userDealers.filter(x => x.dealerId).find(x => x.dealerName === booking.preparerDealerName)?.preparerId
                : typeof formInput.preparerID === "string"
                ? formInput.preparerID
                : typeof formInput.preparerID === "object" && formInput.preparerID != null
                ? formInput.preparerID.preparerID
                : ""
        let preparedByDealer = preparerID !== "NA" && preparerID !== "" && preparerID !== undefined ? true : false

        return {
            ...booking,
            jobNumber: formInput.jobNumber,
            customerOrderRef: formInput.customerOrderRef,
            recipients: formInput.recipients,
            email: formInput.email,
            phone: formInput.phone,
            notes: formInput.notes,
            failureDescription: formInput.failureDescription,
            preparerID: preparerID,
            preparedByDealer: preparedByDealer,
        } as BookingDetail
    }

    /*CUSTOM PRICING Attributes changed*/
    const {testStart, dealerID, customerID, testPackageID, bookingID} = booking
    const testDate = dayjs(testStart).format("YYYY-MM-DD")
    useEffect(() => {
        if (dealerID > 0 && customerID > 0 && testPackageID && testPackageID > 0) {
            dispatch(getCustomPriceForBooking(testDate, dealerID, customerID, testPackageID, bookingID))
        } else {
            dispatch(showCustomPriceForBooking([null, false]))
        }
    }, [dealerID, customerID, testPackageID, testDate, dispatch])

    const testFormSubmitHandler = React.useCallback(
        (values, event: any) => {
            event?.preventDefault()
            //TODO DEL
            if (event.nativeEvent.submitter.id === "btnTestSaveAndClose") {
                const bookingData = values as BookingDetail
                const formData = getFormInputValues(bookingData)

                dispatch(saveBookingDetails(formData, true, location.pathname))
            }
            if (event.nativeEvent.submitter.id === "btnTestSave") {
                const bookingData = values as BookingDetail
                const formData = getFormInputValues(bookingData)
                dispatch(saveBookingDetails(formData, false, location.pathname))
            }
            if (event.nativeEvent.submitter.id === "btnSendConfirmationEmail") {
                const bookingData = values as BookingDetail

                if (bookingNoEdit.email.length > 0) {
                    dispatch(sendConfirmationEmail(bookingData.bookingID, location.pathname))
                } else if (values?.email?.length === 0) {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Email address is required",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                }
            }
            return false
        },
        [dispatch, location.pathname, bookingNoEdit.email],
    )

    const changeBookingState = (booking: BookingDetail, includeFields: boolean = true) => {
        if (includeFields) {
            const formData = getFormInputValues(booking)
            dispatch(setBookingDetail(formData))
        } else {
            dispatch(setBookingDetail(booking))
        }
    }

    const onBookingStatusChange = (event: DropDownListChangeEvent) => {
        const data = event.target.value as BookingStatus
        changeBookingState({...booking, bookingStatusID: data.bookingStatusId})
    }

    const onDealerChange = (event: DropDownListChangeEvent) => {
        const dealerId = event.value.dealerId
        const defaultTestStation = testStations.find((x: TestStation) => x.dealerID === dealerId && x.dealerDefault)
        const selectedPreparerDealerName = calltoPreparerDealerName(userDealers, dealerId)
        let bkdt = {
            ...booking,
            dealerID: parseInt(dealerId),
            testStationID: defaultTestStation?.testStationID ?? null,
            testStationName: defaultTestStation?.testStationName ?? "",
            preparerDealerName: selectedPreparerDealerName === "" ? selectedPreparerDealerName : null,
            preparedByDealer: false,
        }
        bkdt = resetTestPackageAndPricing(bkdt)
        changeBookingState(bkdt)
    }

    const onComboChangevent = (ev: ComboBoxChangeEvent) => {
        const data = ev.target.value
        let bkdt = booking
        if (data !== null) {
            bkdt = {...booking, [`${ev.target.name}`]: data[`${ev.target.name}`]}
        } else {
            bkdt = {...booking, [`${ev.target.name}`]: null}
        }

        if (ev.target.name === "testCode" || ev.target.name === "axleCode") {
            bkdt = resetTestPackageAndPricing(bkdt)
        }
        if (ev.target.name === "preparerDealerName" || ev.target.name === "preparerID") {
            bkdt = {
                ...bkdt,
                preparedByDealer: data != null ? (data["dealerId"] === selectBookingDealer ? true : false) : false,
            }
        }
        changeBookingState(bkdt)
    }
    const failureValidator = (value: string) =>
        !value || value.trim().length === 0 ? "Failure description required" : ""

    const resetTestPackageAndPricing = (booking: BookingDetail) => {
        const bkdt = {
            ...booking,
            testPackageID: 0,
            laneFee: 0,
            testFee: 0,
        }
        return bkdt
    }

    const updateTotalAmount = (laneFees: number) => {
        const bkdt = booking
        changeBookingState({...booking, laneFee: laneFees, total: bkdt.testFee + laneFees})
    }

    const VehicleSectionFields = useMemo(
        () => (
            <VehicleSection
                booking={booking}
                updateBookingDetail={changeBookingState}
                syncFormValues={getFormInputValues}
            />
        ),
        [booking],
    )

    useEffect(() => {
        if (booking.annualTests && !BlobExists) {
            booking.annualTests &&
                booking.annualTests.length > 0 &&
                dispatch(
                    checkBlobCertificateExists(
                        `${booking.registration}-${
                            booking.annualTests?.find(annualTest => annualTest.testResult === "PASSED")
                                ?.testCertificateNumber === undefined
                                ? ""
                                : booking.annualTests.find(at => at.testResult === "PASSED")?.testCertificateNumber
                        }`,
                    ),
                )
        }
    }, [booking.annualTests])

    return (
        <>
            <AlertToastr />
            <MDBModal
                staticBackdrop
                // setShow={setOpenCloseBookingDialog}
                animationDirection="right"
                show={showTestDialog}
                tabIndex="-1"
            >
                <MDBModalDialog position="top right modal-xl" side>
                    <MDBModalContent className="full-height ">
                        <MDBModalHeader>
                            <MDBModalTitle>
                                {booking.bookingID && booking.bookingID > 0 ? booking.bookingRef : "New Booking"}
                            </MDBModalTitle>
                            <MDBBtn
                                color="none"
                                className="btn-close"
                                onClick={() => {
                                    dispatch(toggleTestDialog(false))
                                    dispatch(setBlobExists(false))
                                }}
                            ></MDBBtn>
                        </MDBModalHeader>
                        {(loadingBookingDetail || savingBooking) && <SpinnerFragment />}
                        {showTestDialog && !loadingBookingDetail && booking.bookingTypeID === 1 && (
                            <Form
                                key={JSON.stringify(booking)}
                                initialValues={booking}
                                onSubmit={testFormSubmitHandler}
                                ref={formRef}
                                ignoreModified
                                render={(formRenderProps: FormRenderProps) => (
                                    <FormElement className="w-100">
                                        <Toolbar className="shadow-0 border-bottom p-0 pb-1 pt-1 ps-1 position-sticky top-0">
                                            <ToolbarItem>
                                                <Button
                                                    id="btnTestSaveAndClose"
                                                    disabled={savingBooking || loadingBookingDetail}
                                                    type="submit"
                                                    fillMode="flat"
                                                    themeColor={"primary"}
                                                    iconClass="fa fa-save fa-close"
                                                >
                                                    Save & Close
                                                </Button>
                                            </ToolbarItem>
                                            <ToolbarSeparator />
                                            <ToolbarItem>
                                                <Button
                                                    id="btnTestSave"
                                                    disabled={savingBooking || loadingBookingDetail}
                                                    type="submit"
                                                    fillMode="flat"
                                                    themeColor={"primary"}
                                                    iconClass="far fa-save"
                                                >
                                                    Save
                                                </Button>
                                            </ToolbarItem>
                                            {booking.bookingID > 0 &&
                                                (bookingNoEdit?.email?.split(",")[0].length > 0 ||
                                                    booking?.email?.split(",")[0].length > 0) && (
                                                    <>
                                                        <ToolbarSeparator />
                                                        <ToolbarItem>
                                                            <Button
                                                                id="btnSendConfirmationEmail"
                                                                disabled={
                                                                    !formRenderProps.valid ||
                                                                    savingBooking ||
                                                                    loadingBookingDetail
                                                                }
                                                                type="submit"
                                                                fillMode="flat"
                                                                themeColor={"primary"}
                                                                iconClass="far fa-envelope"
                                                            >
                                                                {!bookingNoEdit.confirmationEmail
                                                                    ? "Send Confirmation"
                                                                    : "Resend Confirmation"}
                                                            </Button>
                                                        </ToolbarItem>
                                                    </>
                                                )}
                                            <ToolbarSpacer />
                                            {booking.bookingID > 0 && (
                                                <ToolbarItem>
                                                    <span className="font-weight-bold">
                                                        <Field
                                                            name={"bookingStatusID"}
                                                            component={BKDropdownField}
                                                            onChange={onBookingStatusChange}
                                                            data={
                                                                booking.isBlocked
                                                                    ? bookingStatusData.filter(
                                                                          x => x.bookingStatusId !== 3,
                                                                      )
                                                                    : bookingStatusData
                                                            }
                                                            stateValue={booking.bookingStatusID}
                                                            textField={"bookingStatusName"}
                                                            dataItemKey={"bookingStatusId"}
                                                            loading={loadingBookingStatus}
                                                            label={""}
                                                        />
                                                    </span>
                                                </ToolbarItem>
                                            )}
                                        </Toolbar>
                                        <MDBModalBody className="modal-overflow">
                                            <fieldset className={"k-form-fieldset mt-0"} style={{marginBottom: "52px"}}>
                                                <Input name="bookingID" type="hidden" />
                                                <Input name="customerId" type="hidden" />
                                                <Input name="confirmationEmail" type="hidden" />
                                                <MDBRow>
                                                    <MDBCol md="3" className="col-border-right">
                                                        {VehicleSectionFields}
                                                    </MDBCol>
                                                    <MDBCol md="9">
                                                        <MDBRow>
                                                            <MDBCol md="6" className="col-border-right">
                                                                <div className="text-muted font-weight-bold">
                                                                    Dealer & Customer
                                                                </div>
                                                                <hr className="title-hr mt-0" />
                                                                <Field
                                                                    name={"dealerID"}
                                                                    label={"Dealer"}
                                                                    component={BKDropdownField}
                                                                    onChange={onDealerChange}
                                                                    data={userDealers}
                                                                    textField="dealerName"
                                                                    dataItemKey="dealerId"
                                                                    stateValue={booking.dealerID}
                                                                    disabled={booking.bookingID > 0}
                                                                />

                                                                <Field
                                                                    allowCustom={false}
                                                                    filterable={true}
                                                                    name="testStationID"
                                                                    component={BKComboBox}
                                                                    label="Test Station"
                                                                    onChange={onComboChangevent}
                                                                    data={testStations}
                                                                    textField="testStationName"
                                                                    dataItemKey="testStationID"
                                                                    stateValue={booking.testStationID}
                                                                    loading={loadingTestStations}
                                                                    required={true}
                                                                />
                                                                <BKPreparerIDComboBox
                                                                    onChange={onComboChangevent}
                                                                    checkBookingID={booking.bookingID > 0}
                                                                    preparerDealerName={booking.preparerDealerName}
                                                                    preparerID={booking.preparerID}
                                                                    isPreparerEditable={booking.isBlocked}
                                                                    bookingDate={dayjs(booking.testStart)
                                                                        .startOf("date")
                                                                        .format("YYYY-MM-DD")}
                                                                />
                                                                <MDBRow>
                                                                    <MDBCol md="6">
                                                                        <Field
                                                                            name="jobNumber"
                                                                            component={TextInputField}
                                                                            label="Job Number"
                                                                            tabIndex={1}
                                                                        />
                                                                    </MDBCol>
                                                                    <MDBCol md="6">
                                                                        <Field
                                                                            name="customerOrderRef"
                                                                            component={TextInputField}
                                                                            label="Customer Order Ref."
                                                                            tabIndex={2}
                                                                        />
                                                                    </MDBCol>
                                                                </MDBRow>
                                                                <CustomerPackagesMultiCombo
                                                                    customer={booking.customer ?? null}
                                                                    booking={booking}
                                                                    updateBookingDetail={changeBookingState}
                                                                />
                                                                <MDBRow>
                                                                    <MDBCol md="12">
                                                                        <Field
                                                                            name="recipients"
                                                                            component={ContactInputField}
                                                                            label="Recipients"
                                                                            tabIndex={3}
                                                                            disabled={true}
                                                                        />
                                                                    </MDBCol>
                                                                </MDBRow>
                                                                <Field
                                                                    name="email"
                                                                    component={ContactInputField}
                                                                    label="Email"
                                                                    tabIndex={5}
                                                                    disabled={true}
                                                                />
                                                                <Field
                                                                    name="phone"
                                                                    component={ContactInputField}
                                                                    label="Phone"
                                                                    tabIndex={6}
                                                                    disabled={true}
                                                                />
                                                            </MDBCol>
                                                            <MDBCol md="6">
                                                                <div className="text-muted font-weight-bold">
                                                                    Test Details
                                                                </div>
                                                                <hr className="title-hr mt-0" />
                                                                <MDBRow>
                                                                    <MDBCol md="6">
                                                                        <BKTestCodeComboBox
                                                                            bookingID={booking.bookingID}
                                                                            testPackageID={booking.testPackageID}
                                                                            onChange={onComboChangevent}
                                                                        />
                                                                    </MDBCol>
                                                                    <MDBCol md="6">
                                                                        <BKAxleComboBox
                                                                            bookingID={booking.bookingID}
                                                                            testPackageID={booking.testPackageID}
                                                                            onChange={onComboChangevent}
                                                                        />
                                                                    </MDBCol>
                                                                </MDBRow>

                                                                <TestPackagesMultiCombo
                                                                    testPackageId={booking.testPackageID}
                                                                    booking={booking}
                                                                    updateBookingDetail={changeBookingState}
                                                                />
                                                                <TestDateSlotSelection
                                                                    booking={booking}
                                                                    updateBookingDetail={changeBookingState}
                                                                />
                                                                <MDBRow>
                                                                    <MDBCol md="6">
                                                                        <Field
                                                                            disabled
                                                                            name="testFee"
                                                                            component={NumberInputField}
                                                                            label="Test fees"
                                                                        />
                                                                    </MDBCol>
                                                                    <MDBCol md="6">
                                                                        <div
                                                                            style={{
                                                                                display: "flex",
                                                                                alignItems: "flex-end",
                                                                            }}
                                                                        >
                                                                            <LaneFeesNumberInput
                                                                                updateTotalAmount={updateTotalAmount}
                                                                            />
                                                                        </div>
                                                                    </MDBCol>
                                                                </MDBRow>
                                                                <MDBRow>
                                                                    <MDBCol md="12">
                                                                        <Field
                                                                            name="total"
                                                                            component={NumberInputField}
                                                                            label="Total"
                                                                            disabled
                                                                        />
                                                                    </MDBCol>
                                                                </MDBRow>
                                                                <MDBRow>
                                                                    <MDBCol md="12">
                                                                        <Field
                                                                            allowCustom={false}
                                                                            filterable={true}
                                                                            name="tranType"
                                                                            component={BKComboBox}
                                                                            label="Transaction Type"
                                                                            onChange={onComboChangevent}
                                                                            data={transactionTypes}
                                                                            textField="tranTypeName"
                                                                            dataItemKey="tranType"
                                                                            stateValue={booking.tranType}
                                                                        />
                                                                    </MDBCol>
                                                                </MDBRow>
                                                            </MDBCol>
                                                            {booking.annualTests &&
                                                                booking.annualTests?.length > 0 &&
                                                                booking.annualTests?.some(
                                                                    a => a.testResult === "FAILED",
                                                                ) && (
                                                                    <Field
                                                                        name="failureDescription"
                                                                        id="failureDescription"
                                                                        component={Input}
                                                                        label="Failure Description"
                                                                        required
                                                                        validator={failureValidator}
                                                                    />
                                                                )}
                                                            <Field
                                                                name="notes"
                                                                id="notes"
                                                                component={TextAreaInput}
                                                                label="Notes"
                                                                required={booking.bookingStatusID === 3}
                                                                bookingID={bookingID}
                                                            />
                                                        </MDBRow>
                                                    </MDBCol>
                                                </MDBRow>
                                            </fieldset>
                                        </MDBModalBody>
                                        <TestBookingFooter
                                            bookingID={booking.bookingID}
                                            updatedBy={booking.updatedBy}
                                            updatedDate={booking.updatedDate}
                                            reminderCall={booking.reminderCall}
                                            testStart={booking.testStart}
                                            sendreminderEmail={booking.sendReminderEmail}
                                            isEmailEmpty={booking?.email?.split(",")[0].length < 1}
                                        />
                                    </FormElement>
                                )}
                            />
                        )}
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
            {booking.customerID > 0 && (
                <DeleteContactModal
                    showDltPopover={showDltPopover}
                    customerContactId={customerContacId}
                    customerId={booking.customerID}
                />
            )}
            <BookingChangeInfoModal booking={booking} />
        </>
    )
}

export default React.memo(TestBookingAddEditModal)
